import { queryOptions, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCardCategory } from '@/lib/cardCategory';

/**
 * Every card column except `description` — the long generated article text,
 * which the listing/hero views never read. Dropping it keeps the payload that
 * ships with the server-rendered page small.
 */
const CARD_LIST_COLUMNS = [
  'id', 'slug', 'name', 'issuer', 'network', 'annual_fee', 'apr_effective', 'apr_nominal',
  'credit_limit_min', 'credit_limit_max', 'interest_free_days', 'age_min', 'income_min',
  'apple_pay', 'google_pay', 'travel_insurance', 'perks', 'terms_url', 'apply_url', 'logo_url',
  'source', 'status', 'boost_priority', 'excerpt', 'benefits', 'drawbacks', 'best_for',
  'cost_example_validated', 'created_at', 'updated_at', 'pros', 'cons', 'external_id',
  'last_synced_at', 'sync_status', 'affiliate_link', 'selling_points', 'is_popular',
  'display_name', 'previous_slugs', 'display_name_locked', 'slug_locked',
  'foreign_tx_fee_percent', 'travel_health_insurance', 'travel_accident_insurance',
  'travel_cancellation_insurance', 'travel_baggage_insurance', 'cashback_program',
  'bonus_program', 'nfc_contactless', 'partner_card', 'atm_domestic_text', 'atm_eurozone_text',
  'atm_international_text', 'travel_discount_text', 'rental_car_discount_text', 'card_type',
  'card_tier', 'is_prepaid', 'current_account_included', 'hotline_available', 'display_config',
  'card_category', 'account_overdraft_rate', 'fx_fee_home_percent', 'fx_fee_intl_percent',
  'atm_fee_percent', 'atm_fee_min_eur', 'rental_car_insurance', 'has_discounts',
].join(', ');

export const cardsQueryOptions = queryOptions({
  queryKey: ['cards'],
  queryFn: async () => {
    const { data, error } = await supabase
      .from('cards')
      .select(CARD_LIST_COLUMNS)
      .eq('status', 'published')
      .order('boost_priority', { ascending: false });

    if (error) throw error;
    return (data || []) as any[];
  },
  staleTime: 5 * 60 * 1000, // 5 minutes - cards don't change frequently
  gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
});

/**
 * Full published card list. Pass `enabled: false` to keep the browser from
 * fetching (and re-rendering) all cards before the visitor needs them — the
 * homepage ships a 12-card server-rendered preview and only upgrades to the
 * full list on the first interaction.
 */
export const useCards = (enabled: boolean = true) =>
  useQuery({ ...cardsQueryOptions, enabled });

/**
 * Only the cards the homepage shows above the fold. This is what the server
 * renders and ships with the HTML; the full list is fetched by the browser
 * right after hydration, so the initial payload stays small.
 */
export const CARDS_PREVIEW_LIMIT = 12;

export const cardsPreviewQueryOptions = queryOptions({
  queryKey: ['cards', 'preview', CARDS_PREVIEW_LIMIT],
  queryFn: async () => {
    const { data, error } = await supabase
      .from('cards')
      .select(CARD_LIST_COLUMNS)
      .eq('status', 'published')
      .order('boost_priority', { ascending: false })
      .limit(CARDS_PREVIEW_LIMIT);

    if (error) throw error;
    return (data || []) as any[];
  },
  staleTime: 5 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
});

/** Total number of published cards, for the freshness/count copy. */
export const publishedCardCountQueryOptions = queryOptions({
  queryKey: ['cards', 'count'],
  queryFn: async () => {
    const { count, error } = await supabase
      .from('cards')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'published');

    if (error) throw error;
    return count ?? 0;
  },
  staleTime: 30 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
});

/** Small factual summary used by the homepage answer block and route metadata. */
export const homepageCardFactsQueryOptions = queryOptions({
  queryKey: ['cards', 'homepage-facts'],
  queryFn: async () => {
    const { data, error } = await supabase
      .from('cards')
      .select('slug, name, display_name, annual_fee, apr_effective, card_category, card_type, is_prepaid')
      .eq('status', 'published');

    if (error) throw error;
    const cards = data || [];
    const lowestAprCard = cards
      .filter((card) => getCardCategory(card) === 'revolving' && typeof card.apr_effective === 'number' && card.apr_effective > 0)
      .sort((a, b) => (a.apr_effective ?? Number.POSITIVE_INFINITY) - (b.apr_effective ?? Number.POSITIVE_INFINITY))[0];

    return {
      totalCards: cards.length,
      noAnnualFeeCards: cards.filter((card) => card.annual_fee === 0).length,
      lowestAprCard: lowestAprCard ? {
        slug: lowestAprCard.slug,
        name: lowestAprCard.display_name || lowestAprCard.name,
        apr: lowestAprCard.apr_effective,
      } : null,
    };
  },
  staleTime: 30 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
});
